import { useState, FormEvent } from 'react';

export default function CTASection() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    scheme: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d4hm382jg2jl50j1s6gg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          phone: '',
          email: '',
          scheme: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 bg-gradient-to-br from-teal-600 via-teal-700 to-teal-800 relative overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="text-white">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              立即行動
            </h2>
            <p className="text-2xl mb-8 text-teal-50">
              別讓機會擦身而過，立即開啟您的香港新里程！
            </p>

            <div className="space-y-6 mb-10">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-checkbox-circle-line text-2xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">免費資格評估</h3>
                  <p className="text-teal-100">專業團隊為您分析最適合的移居方案</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-time-line text-2xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">快速回應</h3>
                  <p className="text-teal-100">12小時內專人聯繫，解答您的疑問</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-shield-check-line text-2xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">全程保密</h3>
                  <p className="text-teal-100">您的個人資料將受到嚴格保護</p>
                </div>
              </div>
            </div>

            <div className="space-y-4 pt-8 border-t border-white/20">
              <div className="flex items-center gap-3">
                <i className="ri-phone-line text-2xl text-white"></i>
                <div>
                  <div className="text-sm text-teal-100">聯絡電話</div>
                  <a href="tel:+85293663961" className="text-xl font-bold hover:text-teal-200 transition-colors cursor-pointer">
                    (852) 9366 3961
                  </a>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <i className="ri-mail-line text-2xl text-white"></i>
                <div>
                  <div className="text-sm text-teal-100">電郵地址</div>
                  <a href="mailto:omnisourcetalent@gmail.com" className="text-xl font-bold hover:text-teal-200 transition-colors cursor-pointer">
                    omnisourcetalent@gmail.com
                  </a>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <i className="ri-map-pin-line text-2xl text-white"></i>
                <div>
                  <div className="text-sm text-teal-100">公司地址</div>
                  <p className="text-lg font-bold">
                    灣仔灣仔道133號卓凌中心11樓<br />
                    11/F, TIMES MEDIA CENTRE,<br />
                    133 WANCHAI ROAD,<br />
                    WANCHAI, HONG KONG
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-3xl p-8 shadow-2xl">
            <h3 className="text-3xl font-bold text-gray-900 mb-6">
              30秒免費評估
            </h3>
            <p className="text-gray-600 mb-8">
              填寫以下資料，我們的專家將為您提供專業建議
            </p>

            <form id="hk-immigration-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  姓名 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-teal-500 focus:outline-none transition-colors text-base"
                  placeholder="請輸入您的姓名"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  聯絡電話 <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  name="phone"
                  required
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-teal-500 focus:outline-none transition-colors text-base"
                  placeholder="請輸入您的電話號碼"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  電郵地址 <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-teal-500 focus:outline-none transition-colors text-base"
                  placeholder="請輸入您的電郵地址"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  感興趣的計劃 <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <select
                    name="scheme"
                    required
                    value={formData.scheme}
                    onChange={(e) => setFormData({...formData, scheme: e.target.value})}
                    className="w-full px-4 py-3 pr-10 border-2 border-gray-200 rounded-lg focus:border-teal-500 focus:outline-none transition-colors appearance-none cursor-pointer text-base bg-white"
                  >
                    <option value="">請選擇計劃</option>
                    <option value="高才通">高端人才通行證計劃（高才通）</option>
                    <option value="專才">輸入內地人才計劃（專才）</option>
                    <option value="優才">優秀人才入境計劃（優才）</option>
                    <option value="投資移民">新資本投資者入境計劃（投資移民）</option>
                    <option value="未確定">未確定，需要評估</option>
                  </select>
                  <i className="ri-arrow-down-s-line absolute right-4 top-1/2 -translate-y-1/2 text-xl text-gray-400 pointer-events-none"></i>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  補充資訊
                </label>
                <textarea
                  name="message"
                  rows={4}
                  maxLength={500}
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-teal-500 focus:outline-none transition-colors resize-none text-base"
                  placeholder="請簡述您的背景或特殊需求（選填，最多500字）"
                ></textarea>
                <div className="text-sm text-gray-500 mt-1 text-right">
                  {formData.message.length}/500
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-4 bg-gradient-to-r from-teal-600 to-teal-700 text-white font-bold rounded-lg hover:from-teal-700 hover:to-teal-800 transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-lg whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin text-xl"></i>
                    提交中...
                  </>
                ) : (
                  <>
                    立即獲取免費評估
                    <i className="ri-send-plane-fill text-xl"></i>
                  </>
                )}
              </button>

              {submitStatus === 'success' && (
                <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4 flex items-center gap-3">
                  <i className="ri-checkbox-circle-fill text-2xl text-green-600"></i>
                  <p className="text-green-800 font-medium">提交成功！我們會盡快與您聯繫。</p>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4 flex items-center gap-3">
                  <i className="ri-error-warning-fill text-2xl text-red-600"></i>
                  <p className="text-red-800 font-medium">提交失敗，請稍後再試或直接致電聯繫我們。</p>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
